'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

// Demo accounts for autofill — backend integration point
const demoAccounts = [
  { role: 'Investor', email: 'amara.osei@vaultis.io', password: 'Vaultis#Inv2026' },
  { role: 'Developer', email: 'tariq.hassan@vaultis.io', password: 'Vaultis#Dev2026' },
  { role: 'Inspector', email: 'priya.nair@vaultis.io', password: 'Vaultis#Insp2026' },
  { role: 'Admin', email: 'admin@vaultis.io', password: 'Vaultis#Adm2026' },
];

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password, rememberMe }
    await new Promise((r) => setTimeout(r, 1200));

    const found = demoAccounts.find(
      (a) => a.email === data.email && a.password === data.password
    );

    if (found) {
      toast.success(`Signed in as ${found.role}`);
      router.push('/role-based-dashboard');
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
    }
    setIsLoading(false);
  };

  const autofill = (account: typeof demoAccounts[0]) => {
    setValue('email', account.email);
    setValue('password', account.password);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Sign in to Vaultis</h1>
        <p className="text-sm text-muted-foreground">Access your role-based dashboard</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-email">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@organization.com"
            className={`w-full px-3 py-2.5 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
              errors.email ? 'border-danger' : 'border-border'
            }`}
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-password">
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`w-full px-3 py-2.5 pr-10 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                errors.password ? 'border-danger' : 'border-border'
              }`}
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-border bg-input accent-primary"
              {...register('rememberMe')}
            />
            <span className="text-sm text-muted-foreground">Remember me</span>
          </label>
          <button type="button" className="text-sm text-primary hover:underline">
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-md text-sm font-semibold hover:bg-primary/90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {/* Demo Accounts */}
      <div className="mt-6 p-4 rounded-xl border border-border bg-muted/20">
        <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
          Development Access
        </p>
        <div className="space-y-1.5">
          {demoAccounts.map((account) => (
            <div
              key={`demo-${account.role}`}
              className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-muted-foreground w-16">{account.role}</span>
                <span className="text-xs text-muted-foreground font-mono">{account.email}</span>
              </div>
              <button
                type="button"
                onClick={() => autofill(account)}
                className="text-xs text-primary hover:underline font-medium"
              >
                Use
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToSignup} className="text-primary hover:underline font-medium">
          Create one
        </button>
      </p>
    </div>
  );
}